import {Component, Inject, OnInit} from '@angular/core';
import {CommonComponent} from "../../common/common-component";
import {ContentBlockComponent} from "../../component/content-block/content-block.component";
import {JsonPipe, NgForOf, NgIf} from "@angular/common";
import {ConfigService} from "../../common/service/config.service";
import {ActivatedRoute, Router} from "@angular/router";
import {AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators} from "@angular/forms";
import {Filter} from "bad-words";

// matches what the BACK-END sends
export type JsonValidator =
  | 'required' | 'email'
  | { min: number } | { max: number }
  | { minLength: number } | { maxLength: number }
  | { pattern: string }
  | { profanity: string[] }

export interface JsonFieldConfig {
  key: string;
  label: string;
  type: 'text' | 'email' | 'number' | 'password';
  validators?: JsonValidator[];
  value?: any;
}

export function profanityFilterLib(extraWords: string[]): ValidatorFn {
  const filter = new Filter();
  if(extraWords.length > 0) {
    filter.addWords(...extraWords);
  }
  return (c: AbstractControl) => filter.isProfane(c.value) ? { profanity: true } : null;
}

/** Turn one JsonValidator into an Angular ValidatorFn */
function mapValidator(v: JsonValidator): ValidatorFn {
  // console.log(v);
  if (v === 'required')  return Validators.required;
  if (v === 'email')     return Validators.email;

  if ('min'        in v) return Validators.min(v.min);
  if ('max'        in v) return Validators.max(v.max);
  if ('minLength'  in v) return Validators.minLength(v.minLength);
  if ('maxLength'  in v) return Validators.maxLength(v.maxLength);
  if ('pattern'    in v) return Validators.pattern(v.pattern);
  if ('profanity'  in v) return profanityFilterLib(v.profanity);

  throw new Error('Unknown validator spec: ' + JSON.stringify(v));
}

@Component({
  selector: 'section#survey',
  standalone: true,
  imports: [
    ContentBlockComponent,
    NgForOf,
    NgIf,
    ReactiveFormsModule,
    JsonPipe
  ],
  templateUrl: './survey.component.html'
})
export class SurveyComponent extends CommonComponent implements OnInit {

  public boothMode: boolean = false;
  public blocks: any;

  public formConfig: any;
  public form!: FormGroup;

  constructor(
    private configService: ConfigService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    @Inject('Storage') protected storage: Storage
  ) {
    super();
    this.boothMode = this.storage.getItem('forceBoothMode') === 'true';
  }

  ngOnInit(): void {
    this.configService.config$.subscribe((config) => {
      this.config = config;
      this.blocks = this.getBlocks();
      // console.log(this.blocks);
      this.formConfig = Object.values(this.blocks).find((block: any) => block.type === 'form');
      // console.log(this.formConfig);
      this.form = this.buildForm(this.formConfig.block);
    });
  }

  getBlocks() {
    const blocks: any = [];
    if (this.config) {
      // console.log(this.config.contentBlocks);
      for (const [slug, block] of Object.entries(this.config.contentBlocks) as [any, any][]) {
        if (block.type === 'form') {
          blocks.push({slug: slug, block: JSON.parse(block.content), type: 'form'});
        } else {
          blocks.push({slug: slug, block: block.content, type: block.type});
        }
      }
    }

    return blocks;
  }

  /** Build a FormGroup from arbitrary FieldConfig[] */
  private buildForm(config: JsonFieldConfig[]): FormGroup {
    // console.log(config);
    const group = this.fb.group({});
    config.forEach(f => {
      // console.log(f);
      const fns = (f.validators ?? []).map(mapValidator); // map fn above
      group.addControl(f.key, this.fb.nonNullable.control(f.value ?? '', fns));
    });
    // console.log(group);
    return group;
  }

  onSubmit() {
    if (this.form.valid) {
      // console.log(this.form.value);
      let payload: any = JSON.parse(this.storage.getItem('user_config') || '{}');
      payload['<!--(survey)--!>'] = this.form.value;
      this.storage.setItem('user_config', JSON.stringify(payload));
      this.router.navigate([ '..', 'capture' ], { relativeTo: this.route }).then();
    } else {
      this.form.markAllAsTouched();
    }
  }

  runChecks(key: string, $event: KeyboardEvent, checks: string[] | undefined) {
    if(checks && checks.length > 0) {
      const target = $event.target as EventTarget & HTMLInputElement;
      if(checks.includes('capitalize_all_words') && target.value) {
        this.form.patchValue({ [key]: target.value.replace(/\b\w/g, (char: string) => char.toUpperCase()) });
      }
      if(checks.includes('uppercase') && target.value) {
        this.form.patchValue({ [key]: target.value.toUpperCase() });
      }
    }
  }
}


